import React from 'react'
import { Link } from 'widgets-for-react'
import { DataLayer } from '../../utils/datalayer'
import { formatDateDDMMYYYY } from '../../utils/date'
import { useInView } from '../../utils/hooks/hooks/useInView'
import { Image } from '../../utils/images'
import { featuredContentSrc, featuredContentSrcSet } from '../../utils/imageSizes'
import { getImageUrls } from '../../utils/sharepointImageUrls'
import { ArticlePage } from '../cmsPage/cmsPageState'
import { ResourceInfo } from '../cmsPage/sharepointPageApi/types/pageData'
import { inview, transformArticleRelevanceForDataLayerObject } from './magazinesUtils'

export type MagazinesOverviewLayoutProps = {
  featuredArticles: ArticlePage[]
  semiFeaturedArticles: ArticlePage[]
  regularArticles: ResourceInfo<ArticlePage>[]
}

export const MagazinesOverviewLayout = (
  props: MagazinesOverviewLayoutProps
) => {
  const { featuredArticles, semiFeaturedArticles, regularArticles } = props

  return (
    <div className="magazines-overview">
      <>
        <FeaturedArticlesLayout
          articles={featuredArticles}
        ></FeaturedArticlesLayout>
        <SemiFeaturedArticlesLayout
          articles={semiFeaturedArticles}
        ></SemiFeaturedArticlesLayout>
        <RegularArticlesLayout
          articles={regularArticles}
        ></RegularArticlesLayout>
      </>
    </div>
  )
}

export const FeaturedArticlesLayout = (props: { articles: ArticlePage[] }) => {
  return (
    <div className="featured__container">
      <div className="featured__inner container--m gutter--m spacing--m">
        <ul className="featured__articles-list">
          {props.articles.map((article, index) => {
            const { inView, ref } = useInView(
              { threshold: inview.threshold },
              (inview) => {
                inView &&
                  DataLayer.magazineArticles(
                    'view',
                    article.magazine,
                    article.title,
                    transformArticleRelevanceForDataLayerObject({
                      kind: 'Featured',
                      position: (index + 1) as 1 | 2
                    })
                  )
              },
              !inview?.triggerOnce
            )
            return (
              <li key={index}>
                <Link
                  onClick={() =>
                    DataLayer.magazineArticles(
                      'click',
                      article.magazine,
                      article.title,
                      transformArticleRelevanceForDataLayerObject({
                        kind: 'Featured',
                        position: (index + 1) as 1 | 2
                      })
                    )
                  }
                  to={article.path}
                  className="featured__article"
                  aria-label={`Lees verder: '${article.title}'`}
                >
                  <div className="featured__content">
                    <header className="featured__header">
                      <time dateTime={article.articleDate.toISOString()} className={'label--m'}>
                        {formatDateDDMMYYYY(article.articleDate)}
                      </time>
                      <h2 key={`featured__article--${index}`} id={`featured__article--${index}`} ref={ref as React.RefObject<HTMLHeadingElement>} className="featured__title title--l">
                        {article.title}
                      </h2>
                    </header>
                    <p className="featured__intro">{article.intro}</p>
                    <span aria-hidden="true" className="featured__button">
                      Lees verder
                    </span>
                  </div>
                  <div className="featured__visual" aria-hidden="true" >
                    <svg
                      className="magazine-icon"
                      width="93px"
                      height="93px"
                      aria-hidden="true"
                    >
                      <use
                        xlinkHref={
                          article.magazine === 'Globe'
                            ? '#icon--corner-g'
                            : '#icon--corner-l'
                        }
                      />
                    </svg>
                    {article.image ? (
                      <Image
                        src={featuredContentSrc}
                        srcset={featuredContentSrcSet}
                        lazyload={false}
                        urls={getImageUrls(article.image)}
                        width={'500'}
                        height={'308'}
                        keepOriginalSize={false}
                        classNames={{
                          image: ''
                        }}
                      />
                    ) : (
                      <img
                        className="no-lazyload"
                        width="500"
                        height="308"
                        alt=""
                        src="/images/empty.png"
                        data-src={
                          '/images/article-header-placeholder-small.png'
                        }
                        data-sizes="100vw"
                      />
                    )}
                  </div>
                </Link>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

export const SemiFeaturedArticlesLayout = (props: {
  articles: ArticlePage[]
}) => {
  return (
    <div className="semifeatured__container">
      <div className="semifeatured__inner container--m gutter--m spacing--m">
        <ul className="semifeatured__articles-list">
          {props.articles.map((article, index) => {
            const { inView, ref } = useInView(
              { threshold: inview.threshold },
              (inview) => {
                inView &&
                  DataLayer.magazineArticles(
                    'view',
                    article.magazine,
                    article.title,
                    transformArticleRelevanceForDataLayerObject(
                      'SemiFeatured'
                    )
                  )
              },
              !inview?.triggerOnce
            )
            return (
              <li key={index} className="semifeatured__article">
                <Link
                  onClick={() =>
                    DataLayer.magazineArticles(
                      'click',
                      article.magazine,
                      article.title,
                      transformArticleRelevanceForDataLayerObject('SemiFeatured')
                    )
                  }
                  to={article.path}
                  className={'grid__link'}
                >
                  <header className="grid__header">
                    <time dateTime={article.articleDate.toISOString()} className={'label--m'}>
                      {formatDateDDMMYYYY(article.articleDate)}
                    </time>
                    <h3
                      key={`semiFeatured__article--${index}`}
                      id={`semiFeatured__article--${index}`}
                      ref={ref as React.RefObject<HTMLHeadingElement>}
                      className="grid__article"
                    >
                      <h3 className="grid__title title--s" id={`${index}`}>
                        {article.title}
                      </h3>
                    </h3>
                  </header>
                  <div className="grid__visual" aria-hidden="true">
                    <svg
                      className="magazine-icon"
                      width="62px"
                      height="62px"
                      aria-hidden="true"
                    >
                      <use
                        xlinkHref={
                          article.magazine === 'Globe'
                            ? '#icon--corner-g'
                            : '#icon--corner-l'
                        }
                      />
                    </svg>
                    {article.image ? (
                      <Image
                        src={featuredContentSrc}
                        srcset={featuredContentSrcSet}
                        lazyload={false}
                        urls={getImageUrls(article.image)}
                        width={'230'}
                        height={'160'}
                        keepOriginalSize={false}
                        classNames={{
                          image: 'semifeatured__image'
                        }}
                      />
                    ) : (
                      <img
                        className="semifeatured__visual no-lazyload"
                        width="320"
                        height="160"
                        alt=""
                        src="/images/empty.png"
                        data-src={
                          '/images/article-header-placeholder-small.png'
                        }
                        data-sizes="100vw"
                      />
                    )}
                  </div>
                </Link>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

export const RegularArticlesLayout = (props: {
  articles: ResourceInfo<ArticlePage>[]
}) => {
  const articles = props.articles

  return (
    <div className="regular__container">
      <div className="container--s gutter--m spacing--m">
        <ul className="links">
          {articles.map((article, index) => {
            const { inView, ref } = useInView(
              { threshold: inview.threshold },
              (inview) => {
                inView &&
                  DataLayer.magazineArticles(
                    'view',
                    article.Resource.magazine,
                    article.Resource.title,
                    transformArticleRelevanceForDataLayerObject()
                  )
              },
              !inview?.triggerOnce
            )
            return (
              <li key={index} className="list__item">
                <article key={`regular__article--${index}`} id={`regular__article--${index}`} ref={ref}>
                  <Link
                    onClick={() =>
                      DataLayer.magazineArticles(
                        'click',
                        article.Resource.magazine,
                        article.Resource.title,
                        transformArticleRelevanceForDataLayerObject()
                      )
                    }
                    to={article.Resource.path}
                    className={'list__link'}
                  >
                    <svg
                      className="magazine-icon"
                      aria-hidden="true"
                      width="56px"
                      height="56px"
                    >
                      <use
                        xlinkHref={
                          article.Resource.magazine === 'Globe'
                            ? '#icon--diagonal-g'
                            : '#icon--diagonal-l'
                        }
                      />
                    </svg>
                    <div>
                      <p className="label--m">
                        {formatDateDDMMYYYY(article.Resource.articleDate)}
                      </p>
                      <p className="list__title title--s">
                        {article.Resource.title}
                      </p>
                    </div>
                  </Link>
                </article>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
